import { gql, useMutation } from '@apollo/client';

import type {
  GetRemittanceTapePostUrl,
  GetRemittanceTapePostUrlVariables,
} from './__generated__/getRemittanceTapePostUrl_gql';
/**
 * Returns a presigned POST URL and form fields for uploading a remittance tape file.
 * Client POSTs the file to postUrl with the returned formFields.
 */
export const GET_REMITTANCE_TAPE_POST_URL = gql`
  mutation GetRemittanceTapePostUrl($input: GetRemittanceTapePostUrlInput!) {
    getRemittanceTapePostUrl(input: $input) {
      postUrl
      upload_id
      metadata {
        key
        value
      }
      formFields {
        key
        value
      }
      fileFieldName
      expiresInSeconds
    }
  }
`;

export function useGetRemittanceTapePostUrl() {
  return useMutation<GetRemittanceTapePostUrl, GetRemittanceTapePostUrlVariables>(
    GET_REMITTANCE_TAPE_POST_URL,
  );
}

export interface RemittanceUploadResult {
  upload_id: string;
}

export interface RemittanceUploadOptions {
  /** Called as soon as upload_id is known (before POST starts), for optimistic UI. */
  onUploadId?: (uploadId: string) => void;
  /** Called with 0–100 during the POST body upload. */
  onProgress?: (percent: number) => void;
}

/**
 * Fetches presigned POST URL and form fields, then POSTs the file to postUrl.
 * Returns upload_id when done. Use onUploadId to add an optimistic row before POST starts.
 */
export async function uploadRemittanceTapeWithPostUrl(
  mutate: (opts: {
    variables: GetRemittanceTapePostUrlVariables;
  }) => Promise<{ data?: GetRemittanceTapePostUrl | null }>,
  file: File,
  dealId: string,
  paymentMonth: string,
  options?: RemittanceUploadOptions,
): Promise<RemittanceUploadResult> {
  const contentType = file.type || 'application/octet-stream';
  const { data } = await mutate({
    variables: {
      input: {
        deal_id: dealId,
        file_name: file.name,
        content_type: contentType,
        payment_month: paymentMonth,
      },
    },
  });

  const result = data?.getRemittanceTapePostUrl as
    | (GetRemittanceTapePostUrl['getRemittanceTapePostUrl'] & { upload_id?: string })
    | null
    | undefined;
  if (!result?.postUrl || !result.formFields || !result.fileFieldName) {
    throw new Error('Invalid response from getRemittanceTapePostUrl');
  }
  const upload_id = result.upload_id ?? '';
  if (!upload_id) {
    throw new Error('Missing upload_id in getRemittanceTapePostUrl response');
  }

  options?.onUploadId?.(upload_id);

  const form = new FormData();
  for (const { key, value } of result.formFields) {
    form.append(key, value);
  }
  form.append(result.fileFieldName, file);

  const onProgress = options?.onProgress;
  if (onProgress) {
    await postWithProgress(result.postUrl, form, onProgress);
  } else {
    const response = await fetch(result.postUrl, { method: 'POST', body: form });
    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
    }
  }

  return { upload_id };
}

function postWithProgress(
  url: string,
  form: FormData,
  onProgress: (percent: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    });
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
    });
    xhr.addEventListener('error', () => reject(new Error('Upload failed')));
    xhr.open('POST', url);
    xhr.send(form);
  });
}
